// S-L10: Line of Credit Dashboard Card
import { useState, useEffect } from 'react';
import { Card, Statistic, Row, Col, Button, Tag, Space, Typography, Tooltip, Spin, Alert, Progress } from 'antd';
import { WalletOutlined, CheckCircleOutlined, ClockCircleOutlined, StopOutlined, LockOutlined } from '@ant-design/icons';
import { creditLineEndpoints, CreditLine, DrawRequest, CreditLineEligibility } from '../api/creditLineEndpoints';
import { useFeature } from '../contexts/FeatureFlagsContext';
import { formatCurrency } from '../utils/formatters';
import { LocTermsDrawer } from './LocTermsDrawer';
import { LocDrawRequestModal } from './LocDrawRequestModal';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export function LocCard() {
  const { t } = useTranslation('loc');
  const isLocEnabled = useFeature('line_of_credit');
  const [loading, setLoading] = useState(true);
  const [creditLine, setCreditLine] = useState<CreditLine | null>(null);
  const [pendingDraw, setPendingDraw] = useState<DrawRequest | null>(null);
  const [eligibility, setEligibility] = useState<CreditLineEligibility | null>(null);
  const [termsDrawerOpen, setTermsDrawerOpen] = useState(false);
  const [drawModalOpen, setDrawModalOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [creditLineResponse, eligibilityResponse] = await Promise.all([
        creditLineEndpoints.getCreditLine(),
        creditLineEndpoints.getEligibility()
      ]);

      setCreditLine(creditLineResponse.credit_line);
      setPendingDraw(creditLineResponse.pending_draw_request);
      setEligibility(eligibilityResponse.eligibility);
    } catch (error) {
      console.error('Failed to fetch LOC data:', error);
    } finally {
      setLoading(false);
    }
  };

  // State: Hidden - Feature disabled AND OB == 0
  if (!isLocEnabled && (!creditLine || creditLine.outstanding_balance_cents === 0)) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <Spin tip={t('messages.loadingData')} />
      </Card>
    );
  }

  // No credit line at all
  if (!creditLine) {
    return null;
  }

  const getStatusTag = () => {
    switch (creditLine.status) {
      case 'active':
        return <Tag icon={<CheckCircleOutlined />} color="success">{t('status.active')}</Tag>;
      case 'frozen':
        return <Tag icon={<LockOutlined />} color="warning">{t('status.frozen')}</Tag>;
      case 'closed':
        return <Tag icon={<StopOutlined />} color="default">{t('status.closed')}</Tag>;
      case 'inactive':
        return <Tag color="default">{t('status.inactive')}</Tag>;
      default:
        return <Tag>{creditLine.status}</Tag>;
    }
  };

  const renderBody = () => {
    // State: Read-only repayment (feature disabled but OB > 0)
    if (!isLocEnabled && creditLine.outstanding_balance_cents > 0) {
      return (
        <Alert
          message={t('messages.featureUnavailable')}
          description={t('messages.featureUnavailableDesc')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      );
    }

    // State: Not eligible (below 3×)
    if (isLocEnabled && eligibility && !eligibility.eligible && creditLine.terms_status !== 'accepted') {
      return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            message={t('messages.notEligible')}
            description={
              <div>
                <p>{t('messages.notEligibleDesc')}</p>
                <p style={{ marginBottom: 8 }}>
                  <strong>{t('progress.label')}:</strong> {formatCurrency(eligibility.base_principal_cents)} / {formatCurrency(eligibility.threshold_cents)}
                </p>
                <Progress percent={Math.min(eligibility.progress_percent, 100)} status="active" />
              </div>
            }
            type="info"
            showIcon
          />
        </Space>
      );
    }

    // State: Eligible - accept terms (button now in header)
    if (isLocEnabled && eligibility && eligibility.eligible && creditLine.terms_status === 'eligible') {
      return null;
    }

    // State: Pending request
    if (pendingDraw) {
      return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            message={t('messages.pendingRequest')}
            description={
              <div>
                <p><strong>{t('progress.amount')}:</strong> {formatCurrency(pendingDraw.requested_amount_cents)}</p>
                <p><strong>{t('progress.status')}:</strong> <Tag icon={<ClockCircleOutlined />} color="processing">{t('progress.pendingApproval')}</Tag></p>
                <p style={{ marginBottom: 0 }}>{t('messages.pendingRequestDesc')}</p>
              </div>
            }
            type="info"
            showIcon
          />
        </Space>
      );
    }

    // State: Active (terms accepted, no pending request)
    return null;
  };

  const getDrawButtonTooltip = () => {
    if (!isLocEnabled) {
      return t('tooltips.unavailable');
    }

    if (creditLine.status === 'frozen') {
      return t('tooltips.frozen');
    }

    if (creditLine.status === 'closed') {
      return t('tooltips.closed');
    }

    if (creditLine.available_credit_cents === 0) {
      return t('tooltips.noCredit');
    }

    if (pendingDraw) {
      return t('messages.pendingRequestDesc');
    }

    if (creditLine.terms_status !== 'accepted') {
      return t('messages.mustAcceptTerms');
    }

    return null;
  };

  const isDrawDisabled = () => {
    return !isLocEnabled ||
      creditLine.status !== 'active' ||
      creditLine.available_credit_cents === 0 ||
      !!pendingDraw ||
      creditLine.terms_status !== 'accepted';
  };

  const drawButton = (
    <Button
      type="primary"
      disabled={isDrawDisabled()}
      onClick={() => setDrawModalOpen(true)}
    >
      {t('buttons.requestCredit')}
    </Button>
  );

  return (
    <>
      <Card
        title={
          <Space>
            <WalletOutlined />
            <span>{t('title')}</span>
            {getStatusTag()}
            {/* Show eligibility status when terms not yet accepted */}
            {isLocEnabled && eligibility && eligibility.eligible && creditLine.terms_status === 'eligible' && (
              <Text style={{ color: '#52c41a', marginLeft: 8 }}>
                <CheckCircleOutlined style={{ marginRight: 4 }} />
                {t('messages.eligible')}
              </Text>
            )}
          </Space>
        }
        extra={
          <Space>
            {/* Show Review & Accept button when eligible */}
            {isLocEnabled && eligibility && eligibility.eligible && creditLine.terms_status === 'eligible' && (
              <Button
                type="primary"
                onClick={() => setTermsDrawerOpen(true)}
              >
                {t('buttons.reviewTerms')}
              </Button>
            )}
            {/* Show Request Credit button when terms accepted and not pending */}
            {creditLine.terms_status === 'accepted' && !pendingDraw && (
              getDrawButtonTooltip() ? (
                <Tooltip title={getDrawButtonTooltip()}>
                  {drawButton}
                </Tooltip>
              ) : (
                drawButton
              )
            )}
          </Space>
        }
      >
        {/* Always show CL/OB/AC */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col flex="1">
            <Statistic
              title={t('metrics.creditLimit')}
              value={creditLine.credit_limit_cents / 100}
              prefix="$"
              precision={2}
            />
          </Col>
          <Col flex="1">
            <Statistic
              title={t('metrics.currentBalance')}
              value={creditLine.outstanding_balance_cents / 100}
              prefix="$"
              precision={2}
              styles={{ content: { color: creditLine.outstanding_balance_cents > 0 ? '#cf1322' : '#3f8600' } }}
            />
          </Col>
          <Col flex="1">
            <Statistic
              title={t('metrics.availableCredit')}
              value={creditLine.available_credit_cents / 100}
              prefix="$"
              precision={2}
              styles={{ content: { color: '#1890ff' } }}
            />
          </Col>
          <Col flex="1">
            <Statistic
              title={t('metrics.interestRate')}
              value={creditLine.current_apr_bps ? (creditLine.current_apr_bps / 100).toFixed(2) : '0.00'}
              suffix="%"
              styles={{ content: { color: '#722ed1' } }}
            />
          </Col>
          <Col flex="1">
            <Statistic
              title={t('metrics.interestPaidYtd')}
              value={creditLine.ytd_interest_cents / 100}
              prefix="$"
              precision={2}
              styles={{ content: { color: '#fa8c16' } }}
            />
          </Col>
        </Row>

        {/* State-specific body */}
        {renderBody()}
      </Card>

      <LocTermsDrawer
        open={termsDrawerOpen}
        onClose={() => setTermsDrawerOpen(false)}
        onAccept={async () => {
          setTermsDrawerOpen(false);
          await fetchData(); // Refresh data
        }}
      />

      <LocDrawRequestModal
        open={drawModalOpen}
        onClose={() => setDrawModalOpen(false)}
        maxAmount={creditLine.available_credit_cents}
        onSuccess={async () => {
          setDrawModalOpen(false);
          await fetchData(); // Refresh data
        }}
      />
    </>
  );
}
